import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DateTime } from 'luxon';
import Icon from '@mdi/react';
import {
    mdiTicket,
    mdiMapMarker,
    mdiMagnify,
    mdiClose,
    mdiCheckCircle,
    mdiCrosshairsGps,
    mdiCalendarMonth,
    mdiChevronRight,
} from '@mdi/js';
import { eventApi } from '../api/event';
import { useAuth } from '../contexts/AuthContext';
import StoreBanner from '../components/store-banner';
import { useMobileMenu } from '../hooks/useMobileMenu';
import danceLocaleIcon from '../assets/dance_locale_3-200h.png';
import SearchTypeSwitcher from '../components/SearchTypeSwitcher';
import CalendarView from '../components/CalendarView';
import BaseModal from '../components/BaseModal';
import { getDanceStyles, resolveToId } from '../config/danceStyles';
import eventPlaceholder from '../assets/event-placeholder.png';
import sampleEventCover from '../assets/backgrounds/preset_BKGND_5.png';
import './FindEvents.css';
import useRememberedSearchLocation, { readRememberedLocation } from "../hooks/useRememberedSearchLocation";

const NEAR_ME_RADIUS_METERS = 20000;

const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a = Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) *
        Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// Ticket-pill label + colour class per backend saleStatus (deriveSaleStatus, the single
// source of truth also used by the event landing page). Only on_sale means "buy now"; the
// other states must NOT show the green "Tickets Available" pill (it contradicted the
// landing page, which correctly said "Ticket sales have ended" / "on sale later"). Copy
// mirrors the landing page's PILL_COPY. A status not in this map falls back to the on_sale
// pill at render (see the fallback note below): the strip is filtered to ticketed events, so
// no_tickets never reaches here, and a missing/unknown status means the backend computed none
// (best treated as available rather than hiding a genuinely-ticketed event's pill).
const TICKET_PILL = {
    on_sale: { label: 'Tickets Available', className: 'ticket-pill-on-sale' },
    coming_soon: { label: 'Coming soon', className: 'ticket-pill-coming-soon' },
    ended: { label: 'Sales ended', className: 'ticket-pill-ended' },
    sold_out: { label: 'Sold out', className: 'ticket-pill-sold-out' },
};

/**
 * /events — public event discovery page.
 *
 * Layout: the strip ("Happening in the Next 10 Days") + month calendar moved
 * verbatim from the old /tickets dashboard view, with the unified search hero
 * (query/location/style/Near Me + search-type switcher) added on top. The
 * filters narrow BOTH the strip and the calendar client-side.
 */
const FindEvents = () => {
    useMobileMenu();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const [searchQuery, setSearchQuery] = useState(searchParams.get('query') || '');
    // A ?location= in the URL always wins (a shared link means THAT place); only
    // fall back to what this browser last searched.
    const urlLocation = searchParams.get('location') || "";
    const remembered = urlLocation ? null : readRememberedLocation();
    const [searchLocation, setSearchLocation] = useState(urlLocation || remembered?.location || '');
    // Text filters apply on Search submit; style + Near Me apply live.
    const [appliedQuery, setAppliedQuery] = useState(searchParams.get('query') || '');
    // Seeded the same way as searchLocation: otherwise the box would show a
    // remembered place that was never actually applied to the results.
    const [appliedLocation, setAppliedLocation] = useState(urlLocation || remembered?.location || '');
    const [selectedStyle, setSelectedStyle] = useState('');
    const [nearMe, setNearMe] = useState(false);

    // Remember the location for next visit. Persists appliedLocation — the value that
    // actually drives the results — so what is restored is what was really searched.
    useRememberedSearchLocation({
        location: appliedLocation,
        urlHadLocation: Boolean(urlLocation),
    });
    const [userLocation, setUserLocation] = useState(null);
    const [locationLoading, setLocationLoading] = useState(false);

    // Strip + calendar state (moved from the old /tickets dashboard view)
    const [calendarEvents, setCalendarEvents] = useState([]);
    const [isLoadingCalendar, setIsLoadingCalendar] = useState(false);
    const [upcomingTicketedEvents, setUpcomingTicketedEvents] = useState([]);
    const [isStripLoaded, setIsStripLoaded] = useState(false);
    // Search-results state: when a filter is active we fetch a wide forward window
    // (not just the displayed month) so a match months out still surfaces as a card.
    const [searchPool, setSearchPool] = useState([]);
    const [isLoadingSearch, setIsLoadingSearch] = useState(false);
    // Multi-event day chooser: { date, events } when a clicked day has >1 event, else null
    const [dayChooser, setDayChooser] = useState(null);
    const [currentDate, setCurrentDate] = useState(DateTime.local());
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        // Handle window resize for mobile detection
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Strip teaser: "Happening in the Next 10 Days" — a fixed, today-anchored
    // list, independent of which month the calendar grid is showing. The calendar
    // beside it handles month browsing, so the strip stays a short imminent-events
    // teaser (no this-week/weekend filters — those are a mobile-only need).
    const loadUpcomingStrip = useCallback(async () => {
        try {
            const now = DateTime.local();
            const startIso = now.startOf("day").toISO();
            const endIso = now.plus({ days: 10 }).endOf("day").toISO();
            const events = await eventApi.getCalendarEvents(startIso, endIso);
            const ticketed = (events || [])
                .filter(event => event.hasTickets || event.ticketTypes?.length > 0)
                .sort((a, b) =>
                    DateTime.fromISO(a.eventStartDateTime).toMillis() -
                    DateTime.fromISO(b.eventStartDateTime).toMillis()
                );
            setUpcomingTicketedEvents(ticketed.slice(0, 10));
        } catch (error) {
            console.error("Error loading upcoming events strip:", error);
        } finally {
            setIsStripLoaded(true);
        }
    }, []);

    // On first load, if the current month has no events, advance the grid to the
    // earliest upcoming event's month so visitors don't land on an empty calendar.
    // Uses a wider look-ahead than the 10-day strip so a first event months out is
    // still found. Runs once; user navigation thereafter is never overridden.
    const jumpToFirstPopulatedMonth = useCallback(async () => {
        try {
            const now = DateTime.local();
            const startIso = now.startOf("month").startOf("day").toISO();
            const endIso = now.plus({ months: 6 }).endOf("month").endOf("day").toISO();
            const events = await eventApi.getCalendarEvents(startIso, endIso);
            if (!events || events.length === 0) return;
            const startOfThisMonth = now.startOf("month");
            const firstUpcoming = events
                .map(e => DateTime.fromISO(e.eventStartDateTime))
                .filter(d => d.isValid && d.startOf("month") >= startOfThisMonth)
                .sort((a, b) => a.toMillis() - b.toMillis())[0];
            if (firstUpcoming && firstUpcoming.startOf("month") > startOfThisMonth) {
                setCurrentDate(firstUpcoming);
            }
        } catch (error) {
            console.error("Error resolving first populated month:", error);
        }
    }, []);

    // Calendar grid: fetch only the displayed month's events (like the public
    // instructor availability calendar). Lets visitors browse to any future month
    // (e.g. Christmas) without pre-loading a fixed multi-month blob.
    const loadCalendarMonth = useCallback(async (month) => {
        setIsLoadingCalendar(true);
        try {
            const startIso = month.startOf("month").startOf("day").toISO();
            const endIso = month.endOf("month").endOf("day").toISO();
            const events = await eventApi.getCalendarEvents(startIso, endIso);
            setCalendarEvents(events || []);
        } catch (error) {
            console.error("Error loading calendar month:", error);
            setCalendarEvents([]);
        } finally {
            setIsLoadingCalendar(false);
        }
    }, []);

    // Search-results pool: a wide forward window (today → +12 months) fetched once
    // a search/filter is active, so matches beyond the displayed month or the 10-day
    // strip still appear as result cards (mirrors the instructor search's grid).
    const loadSearchPool = useCallback(async () => {
        setIsLoadingSearch(true);
        try {
            const now = DateTime.local();
            // Start 24h back (not midnight) to absorb timezone skew — keeps an event
            // that's still "today/upcoming" in another zone from being dropped, while
            // genuinely past events (yesterday and prior) stay excluded.
            const startIso = now.minus({ hours: 24 }).toISO();
            const endIso = now.plus({ months: 12 }).endOf("day").toISO();
            const events = await eventApi.getCalendarEvents(startIso, endIso);
            setSearchPool(events || []);
        } catch (error) {
            console.error("Error loading search pool:", error);
            setSearchPool([]);
        } finally {
            setIsLoadingSearch(false);
        }
    }, []);

    // Kick off the strip teaser and the one-time auto-jump to the first populated
    // month. The calendar grid itself loads via the month-keyed effect below.
    useEffect(() => {
        loadUpcomingStrip();
        jumpToFirstPopulatedMonth();
    }, [loadUpcomingStrip, jumpToFirstPopulatedMonth]);

    // Reload the calendar grid whenever the displayed month changes. Keyed on
    // year+month so the auto-jump (which sets a full date) and the prev/next nav
    // both trigger exactly one refetch per distinct month.
    useEffect(() => {
        loadCalendarMonth(currentDate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentDate.year, currentDate.month]);

    // Search/filter logic — narrows BOTH the strip and the calendar client-side.
    const filterEvents = useCallback((events) => {
        let results = events;

        if (appliedQuery) {
            const q = appliedQuery.toLowerCase();
            results = results.filter(ev =>
                (ev.title || ev.name || '').toLowerCase().includes(q) ||
                (ev.eventLocation?.description || '').toLowerCase().includes(q)
            );
        }

        if (appliedLocation) {
            const loc = appliedLocation.toLowerCase();
            results = results.filter(ev => (ev.eventLocation?.description || '').toLowerCase().includes(loc));
        }

        if (selectedStyle) {
            results = results.filter(ev =>
                (ev.danceStyles || []).some(s => resolveToId(s) === selectedStyle)
            );
        }

        if (nearMe && userLocation) {
            results = results.filter(ev => {
                const loc = ev.eventLocation;
                if (!loc?.latitude || !loc?.longitude) return false;
                return calculateDistance(
                    userLocation.latitude, userLocation.longitude,
                    loc.latitude, loc.longitude
                ) <= NEAR_ME_RADIUS_METERS;
            });
        }

        return results;
    }, [appliedQuery, appliedLocation, selectedStyle, nearMe, userLocation]);

    const filteredStrip = useMemo(
        () => filterEvents(upcomingTicketedEvents),
        [filterEvents, upcomingTicketedEvents]
    );
    const filteredCalendarEvents = useMemo(
        () => filterEvents(calendarEvents),
        [filterEvents, calendarEvents]
    );

    const hasActiveFilters = !!(appliedQuery || appliedLocation || selectedStyle || nearMe);

    // Fetch the wide search pool the moment a filter becomes active (and refresh it
    // each time the active filter set changes — keeps it current without polling).
    useEffect(() => {
        if (hasActiveFilters) loadSearchPool();
    }, [hasActiveFilters, appliedQuery, appliedLocation, selectedStyle, nearMe, loadSearchPool]);

    // Matching events across the whole forward window, de-duped and date-sorted.
    const searchResults = useMemo(() => {
        if (!hasActiveFilters) return [];
        const matched = filterEvents(searchPool);
        const seen = new Set();
        return matched
            .filter(ev => (ev.id && !seen.has(ev.id) && seen.add(ev.id)))
            .sort((a, b) =>
                DateTime.fromISO(a.eventStartDateTime).toMillis() -
                DateTime.fromISO(b.eventStartDateTime).toMillis()
            );
    }, [hasActiveFilters, filterEvents, searchPool]);

    const handleSearch = (e) => {
        if (e) e.preventDefault();
        setAppliedQuery(searchQuery);
        setAppliedLocation(searchLocation);
    };

    const clearSearch = () => {
        setSearchQuery('');
        setSearchLocation('');
        setSelectedStyle('');
        setNearMe(false);
        setAppliedQuery('');
        setAppliedLocation('');
    };

    const toggleNearMe = () => {
        if (!nearMe && !userLocation) {
            setLocationLoading(true);
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    setUserLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
                    setNearMe(true);
                    setLocationLoading(false);
                },
                () => {
                    toast.error('Location access denied. Enable location permissions to use Near Me.');
                    setLocationLoading(false);
                }
            );
        } else {
            setNearMe(prev => !prev);
        }
    };

    // Event interaction handlers
    // Every event — strip card, calendar bar, chooser row — routes the same way:
    // straight to the event's landing/buy page. Full-page navigation: /events/:id
    // is the backend-rendered landing page (proxy route) — navigate() would miss it.
    const handleEventClick = (event) => {
        window.location.href = `/events/${event.id}`;
    };

    // Day-cell click on the events calendar: a day click should move the visitor
    // toward the event, not into a plain day-list modal.
    // 1 event → go straight to it; multiple → show a styled chooser.
    const handleCalendarDayClick = (day, dayEvents) => {
        if (!dayEvents || dayEvents.length === 0) return;
        if (dayEvents.length === 1) {
            handleEventClick(dayEvents[0]);
            return;
        }
        setDayChooser({ date: day, events: dayEvents });
    };

    return (
        <div className="find-events">
            <div data-role="Header" className="home-header-container">
                <header className="home-header">
                    <a href="/" className="home-logo1">
                        <img alt="logo" src={danceLocaleIcon} className="home-image1" />
                        <span className="nav-wordmark">Dance Localé</span>
                    </a>
                    <div className="home-menu1">
                        <a href="/" className="home-text10" style={{ textDecoration: "none" }}>
                            Home
                        </a>
                        <a
                            href="/instructors"
                            className="home-text11"
                            style={{ color: "var(--dl-color-orange-default)", textDecoration: "none" }}
                        >
                            Search
                        </a>
                        {user && user.organisationId && (
                            <a href="/dashboard" className="home-text12" style={{ textDecoration: "none" }}>
                                Dashboard
                            </a>
                        )}
                        {user && (
                            <a href="/my-hub" className="home-text12" style={{ textDecoration: "none" }}>
                                My Hub
                            </a>
                        )}
                        <a href="/contact" className="home-text13" style={{ textDecoration: "none" }}>
                            Contact
                        </a>
                    </div>
                    <div className="home-container11">
                        <StoreBanner />
                    </div>
                    <div data-role="BurgerMenu" className="home-burger-menu">
                        <svg viewBox="0 0 1024 1024" className="home-menu2">
                            <path d="M810.667 725.333h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
                            <path d="M810.667 426.667h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
                            <path d="M810.667 128h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
                        </svg>
                    </div>
                </header>

                {/* Mobile Menu */}
                <div data-role="MobileMenu" className="home-mobile-menu">
                    <div className="home-top">
                        <div className="home-logo2">
                            <img alt="logo" src={danceLocaleIcon} className="home-image2" />
                        </div>
                        <div data-role="CloseMobileMenu" className="home-close-menu">
                            <svg viewBox="0 0 1024 1024" className="home-icon">
                                <path d="M810 274l-238 238 238 238-60 60-238-238-238 238-60-60 238-238-238-238 60-60 238 238 238-238z"></path>
                            </svg>
                        </div>
                    </div>
                    <div className="home-mid">
                        <a href="/" className="home-text14">
                            Home
                        </a>
                        <a href="/instructors" className="home-text15" style={{ color: "var(--dl-color-orange-default)" }}>
                            Search
                        </a>
                        {user && user.organisationId && (
                            <a href="/dashboard" className="home-text16">
                                Dashboard
                            </a>
                        )}
                        {user && (
                            <a href="/my-hub" className="home-text16">
                                My Hub
                            </a>
                        )}
                        <a href="/contact" className="home-text17">
                            Contact
                        </a>
                    </div>
                </div>
            </div>

            {/* Hero Search Section — aligned with /instructors and /schools */}
            <div className="search-hero">
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '6px' }}>
                    {/* <button
                        onClick={() => navigate('/')}
                        style={{
                            position: 'absolute',
                            left: 0,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: 'linear-gradient(135deg, var(--dl-color-purple-default) 0%, var(--dl-color-purple-1000) 100%)',
                            border: '1px solid var(--dl-color-purple-default)',
                            color: 'white',
                            borderRadius: '18px',
                            padding: '8px 16px',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            cursor: 'pointer',
                        }}
                    >
                        ← Back to Home
                    </button> */}
                    <h1 style={{ margin: 0 }}>Find Dance Events</h1>
                </div>
                <p>Discover upcoming dance events and buy tickets</p>


                <form className="search-form" onSubmit={handleSearch}>
                    <div className="search-inputs">
                        <SearchTypeSwitcher active="events" />

                        <div className="search-input-wrapper">
                            <Icon path={mdiTicket} size={1} className="search-input-icon" />
                            <input
                                type="text"
                                placeholder="Event name, dance style..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="search-input"
                            />
                        </div>

                        <div className="search-input-wrapper">
                            <Icon path={mdiMapMarker} size={1} className="search-input-icon" />
                            <input
                                type="text"
                                placeholder="City or region..."
                                value={searchLocation}
                                onChange={(e) => setSearchLocation(e.target.value)}
                                className="search-input"
                            />
                        </div>

                        <button type="submit" className="btn-search">
                            <Icon path={mdiMagnify} size={1} />
                            Search
                        </button>
                    </div>

                    <div className="filter-chips">
                        <select
                            className="filter-select"
                            value={selectedStyle}
                            onChange={(e) => setSelectedStyle(e.target.value)}
                        >
                            <option value="">All styles</option>
                            {getDanceStyles().map(style => (
                                <option key={style.id} value={style.id}>{style.label}</option>
                            ))}
                        </select>

                        <button
                            type="button"
                            className={`filter-chip ${nearMe ? 'active' : ''}`}
                            onClick={toggleNearMe}
                            disabled={locationLoading}
                        >
                            <Icon path={mdiCrosshairsGps} size={0.8} />
                            {locationLoading ? 'Locating...' : 'Near Me (20km)'}
                            {nearMe && <Icon path={mdiCheckCircle} size={0.7} />}
                        </button>

                        {(searchQuery || searchLocation || selectedStyle || nearMe) && (
                            <button type="button" onClick={clearSearch} className="btn-clear">
                                <Icon path={mdiClose} size={0.8} />
                                Clear
                            </button>
                        )}
                    </div>
                </form>
            </div>

            {/* Main Content — strip + calendar (moved from the old /tickets view) */}
            <main className="tickets-content-container">
                {/* Search Results grid — appears whenever a search/filter is active.
                    Mirrors the instructor search: a wrapped grid of matching event
                    cards across the whole forward window, not just the 10-day strip. */}
                {hasActiveFilters && (
                    <section className="dashboard-quick-access">
                        <h2 className="dashboard-section-title">
                            Search Results{!isLoadingSearch ? ` (${searchResults.length})` : ''}
                        </h2>
                        {isLoadingSearch ? (
                            <p className="dashboard-filter-empty">Searching events...</p>
                        ) : searchResults.length === 0 ? (
                            <p className="dashboard-filter-empty">No upcoming events match your filters.</p>
                        ) : (
                            <div className="search-results-grid">
                                {searchResults.map(event => (
                                    <div
                                        key={event.id}
                                        className="ticketed-event-card"
                                        onClick={() => handleEventClick(event)}
                                    >
                                        <div className="ticketed-event-image">
                                            <img
                                                src={event.eventImageUrl || eventPlaceholder}
                                                alt={event.title || event.name}
                                                onError={(e) => {
                                                    e.target.src = eventPlaceholder;
                                                    e.target.onerror = null;
                                                }}
                                            />
                                        </div>
                                        <div className="ticketed-event-content">
                                            <h3 className="ticketed-event-title">{event.title || event.name}</h3>
                                            <p className="ticketed-event-location">
                                                {event.eventLocation?.description || "Location TBA"}
                                            </p>
                                            <p className="ticketed-event-date">
                                                {DateTime.fromISO(event.eventStartDateTime).toFormat("MMM dd, yyyy")}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                )}

                {/* Upcoming Ticketed Events teaser — browsing state only (no active search) */}
                {!hasActiveFilters && filteredStrip.length > 0 && (
                    <section className="dashboard-quick-access">
                        <h2 className="dashboard-section-title">Happening in the Next 10 Days</h2>
                        <div className="dashboard-horizontal-scroll">
                            {filteredStrip.map(event => (
                                <div
                                    key={event.id}
                                    className="ticketed-event-card"
                                    onClick={() => handleEventClick(event)}
                                >
                                    <div className="ticketed-event-image">
                                        <img
                                            src={event.eventImageUrl || eventPlaceholder}
                                            alt={event.title || event.name}
                                            onError={(e) => {
                                                console.warn('[FindEvents] Image failed to load for event:', event.id, 'url:', event.eventImageUrl);
                                                e.target.src = eventPlaceholder;
                                                e.target.onerror = null;
                                            }}
                                        />
                                        {(() => {
                                            // Show the real sale-state; default to on_sale only
                                            // when the backend computed no status (keeps the
                                            // prior pill for events that plainly have tickets).
                                            const pill = TICKET_PILL[event.saleStatus] || TICKET_PILL.on_sale;
                                            return (
                                                <div className={`tickets-available-badge ${pill.className}`}>
                                                    {pill.label}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                    <div className="ticketed-event-content">
                                        <h3 className="ticketed-event-title">{event.title || event.name}</h3>
                                        <p className="ticketed-event-location">
                                            {event.eventLocation?.description || "Location TBA"}
                                        </p>
                                        <p className="ticketed-event-date">
                                            {DateTime.fromISO(event.eventStartDateTime).toFormat("MMM dd, yyyy")}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Example card — browsing state with an empty strip, so a first-time
                    visitor sees what a listed event looks like. No DB record: it opens the
                    backend's display-only GET /events/example page. Disappears once a real event is listed. */}
                {!hasActiveFilters && isStripLoaded && filteredStrip.length === 0 && (
                    <section className="dashboard-quick-access">
                        <h2 className="dashboard-section-title">Happening in the Next 10 Days</h2>
                        <p className="dashboard-filter-empty">
                            No ticketed events in the next 10 days yet. Here's how an event looks once an organiser lists one.
                        </p>
                        <div className="dashboard-horizontal-scroll">
                            <div
                                className="ticketed-event-card"
                                aria-label="Example event"
                                onClick={() => handleEventClick({ id: 'example' })}
                            >
                                <div className="ticketed-event-image">
                                    <img src={sampleEventCover} alt="Foxy Roxy and Fox Loco dancing" />
                                    <div className="tickets-available-badge ticket-pill-example">Example</div>
                                </div>
                                <div className="ticketed-event-content">
                                    <h3 className="ticketed-event-title">Dance Localé Social</h3>
                                    <p className="ticketed-event-location">Your city</p>
                                    <p className="ticketed-event-date">
                                        {DateTime.local().plus({ days: 7 }).toFormat("MMM dd, yyyy")}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Calendar Section */}
                <section>
                    <h2 className="dashboard-section-title">All Events Calendar</h2>
                    <div className={`calendar-section ${isMobile ? 'calendar-section-mobile' : ''} ${isLoadingCalendar ? 'calendar-section-loading' : ''}`}>
                        <CalendarView
                            events={filteredCalendarEvents}
                            compact={false}
                            viewType="monthly"
                            currentDate={currentDate}
                            onEventClick={handleEventClick}
                            onDayClick={handleCalendarDayClick}
                            onPreviousMonth={setCurrentDate}
                            onNextMonth={setCurrentDate}
                            isMobile={isMobile}
                            disablePastDates={true}
                        />
                    </div>
                </section>
            </main>

            {/* Multi-event day chooser — only when a clicked day has more than one event */}
            <BaseModal
                isOpen={!!dayChooser}
                onClose={() => setDayChooser(null)}
                title={dayChooser ? `Events on ${dayChooser.date.toFormat("EEEE, MMMM d")}` : ""}
                icon={mdiCalendarMonth}
                footer={
                    <button className="base-modal-cancel-btn" onClick={() => setDayChooser(null)}>
                        Close
                    </button>
                }
            >
                <div className="day-chooser-list">
                    {dayChooser?.events.map((event, index) => {
                        let time = "";
                        try {
                            time = DateTime.fromISO(
                                typeof event.eventStartDateTime === "string"
                                    ? event.eventStartDateTime
                                    : new Date(event.eventStartDateTime).toISOString()
                            ).toFormat("h:mm a");
                        } catch (_) { /* leave time blank */ }
                        return (
                            <button
                                key={event.id || index}
                                className="day-chooser-row"
                                onClick={() => { setDayChooser(null); handleEventClick(event); }}
                            >
                                <div className="day-chooser-row-main">
                                    <span className="day-chooser-row-title">{event.title || event.name}</span>
                                    <span className="day-chooser-row-meta">
                                        {time}
                                        {time && event.eventLocation?.description ? " · " : ""}
                                        {event.eventLocation?.description || ""}
                                    </span>
                                </div>
                                <Icon path={mdiChevronRight} size={1} className="day-chooser-row-chevron" />
                            </button>
                        );
                    })}
                </div>
            </BaseModal>
        </div>
    );
};

export default FindEvents;
